import { ChefHat } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `apps/web-admin/src/routes/production/production-sheet-view-page.tsx`:
 * button labels and states quoted verbatim.
 * `requiredPermission: 'production.read'` matches the `NavPermission` that
 * gates "Producción" in `layout/navigation.ts`.
 */
export const PRODUCTION_MODULE: GuideModule = {
  id: 'production',
  title: 'Producción',
  icon: ChefHat,
  requiredPermission: 'production.read',
  priority: 8,
  summary:
    'La planilla de producción es el consolidado de todo lo que hay que cocinar para una fecha, armado a partir de los pedidos confirmados. Mientras el corte de esa fecha sigue abierto, la planilla es provisoria: puede cambiar. Cuando estás listo, hacés clic en "Cerrar planilla" para congelarla: esa es la versión definitiva que usa cocina. Si después del cierre se aprueba una solicitud de cambio sobre un pedido de esa fecha, el cambio se registra como anexo, sin tocar la planilla original. Desde acá también imprimís o exportás la planilla para llevarla a cocina.',
  steps: [
    {
      title: 'Revisar la planilla provisoria antes del cierre',
      description:
        'Entrá a Producción, elegí el "Punto de venta" (o "Todos los puntos" para la vista consolidada, de solo lectura) y la fecha. Cada punto tiene su propia planilla. Mientras el corte sigue abierto, la planilla dice "Provisoria — puede cambiar hasta el cierre".',
      keyTerms: ['planilla-provisoria', 'planilla-de-produccion'],
    },
    {
      title: 'Cerrar la planilla',
      description:
        'Cuando el corte de esa fecha ya está cerrado, hacé clic en "Cerrar planilla" y confirmá el cierre. El cierre es para el punto de venta y la fecha que ves en pantalla, y después no se puede reabrir.',
      keyTerms: ['planilla-cerrada', 'cierre-de-corte'],
    },
    {
      title: 'Registrar cambios posteriores al cierre',
      description:
        'Si después del cierre se aprobó una solicitud de cambio para esa fecha, hacé clic en "Registrar cambios posteriores": queda como anexo, sin modificar la planilla original.',
      keyTerms: ['anexo', 'solicitud-de-cambio'],
    },
    {
      title: 'Imprimir la planilla para cocina',
      description: 'Hacé clic en "Imprimir" para abrir la vista lista para llevar a cocina.',
    },
    {
      title: 'Exportar el consolidado',
      description:
        'Hacé clic en "Exportar CSV" para descargar el consolidado y abrirlo en una planilla de cálculo.',
      keyTerms: ['exportar-csv'],
    },
  ],
  states: [
    {
      value: 'PROVISIONAL',
      label: 'Provisoria',
      description: 'Todavía puede cambiar: refleja los pedidos confirmados hasta el momento.',
    },
    {
      value: 'CLOSED',
      label: 'Cerrada',
      description: 'Quedó congelada. Es la versión definitiva que usa cocina para esa fecha.',
    },
  ],
  faq: [
    {
      question: '¿Por qué no puedo cerrar la planilla?',
      answer:
        'Puede ser porque todavía tenés pedidos pendientes de confirmar para esa fecha: primero cerrá el corte desde el Tablero del día, y recién ahí vas a poder cerrar la planilla.',
    },
    {
      question: '¿Qué es un anexo?',
      answer:
        'Es el registro de un cambio en un pedido confirmado que llegó después de cerrar la planilla. No modifica la planilla cerrada: se suma aparte, para que cocina sepa exactamente qué cambió.',
    },
    {
      question: '¿La planilla provisoria ya sirve para cocinar?',
      answer:
        'Puede usarse como referencia, pero todavía puede cambiar hasta que el corte se cierre. La versión definitiva es siempre la planilla cerrada.',
    },
    {
      question: '¿Qué diferencia hay entre el consolidado por menú y por tipo de servicio?',
      answer:
        'El consolidado por menú suma las cantidades por plato; el consolidado por tipo de servicio las agrupa por desayuno, almuerzo, vianda o cena. Elegís la vista con las pestañas de la pantalla.',
    },
  ],
  tips: [
    'Esperá a que se cierre el corte antes de intentar cerrar la planilla: es el mismo orden que sigue el flujo diario.',
    'Revisá siempre los avisos de dietaria y alérgenos antes de imprimir: evita errores en cocina.',
    'Usá "Registrar cambios posteriores" en cuanto se apruebe una solicitud de cambio: mantiene el anexo al día.',
    'Imprimí la planilla recién después de cerrarla, para no llevar a cocina una versión que todavía puede cambiar.',
  ],
};
